import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface CreditContextType {
  credits: number;
  setCredits: (credits: number) => void;
  showLowCreditPopup: boolean;
  setShowLowCreditPopup: (show: boolean) => void;
  showCreditWarning: boolean;
  setShowCreditWarning: (show: boolean) => void;
  isBlocked: boolean;
  shouldShowWarning: boolean;
  subscriptionPlans: SubscriptionPlan[];
  setSubscriptionPlans: (plans: SubscriptionPlan[]) => void;
  currentSubscription: Subscription | null;
  setCurrentSubscription: (subscription: Subscription | null) => void;
  overdraftUsed: boolean;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  amount: number;
  tokens_included: number;
  cost_per_1k_tokens: number;
  savings_percentage: number;
  is_popular: boolean;
  is_recommended: boolean;
  features: string[];
  priority_support: boolean;
  premium_templates: boolean;
  exclusive_features: boolean;
  monthly_consultation: boolean;
  dedicated_support: boolean;
  beta_features: boolean;
}

interface Subscription {
  id: string;
  external_id: string;
  plan: SubscriptionPlan;
  status: string;
  amount: number;
  tokens_included: number;
  next_billing_date: string;
  created_at: string;
  cancelled_at?: string;
  auto_renew?: boolean;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

// Limites para exibição do popup
const CREDIT_BLOCK_LIMIT = 5;     // Bloqueia abaixo de 5
const CREDIT_THRESHOLD_50 = 50;   // Primeiro aviso em 50
const CREDIT_THRESHOLD_30 = 30;   // Segundo aviso em 30
const CREDIT_ALWAYS_SHOW = 30;    // Sempre mostra abaixo de 30

export const CreditProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [credits, setCredits] = useState<number>(0);
  const [previousCredits, setPreviousCredits] = useState<number | null>(null);
  const [showLowCreditPopup, setShowLowCreditPopup] = useState(false);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);

  // Calcular estados baseados nos créditos
  const isBlocked = credits < 0 || (!!user?.overdraft_used && credits < CREDIT_BLOCK_LIMIT);

  // Mostrar aviso apenas abaixo de 30 créditos
  const shouldShowWarning = credits < CREDIT_ALWAYS_SHOW && credits > 0 && !isBlocked;

  // Atualizar créditos baseado no usuário
  useEffect(() => {
    if (user?.available_credits !== undefined) {
      setPreviousCredits(credits);
      setCredits(user.available_credits);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gerenciar popup de bloqueio (sempre mostra quando bloqueado)
  useEffect(() => {
    if (isBlocked && !showLowCreditPopup) {
      setTimeout(() => {
        setShowLowCreditPopup(true);
      }, 100);
    }
  }, [isBlocked, showLowCreditPopup]);

  // Gerenciar popup nos thresholds específicos (50, 30, e sempre abaixo de 30)
  useEffect(() => {
    if (previousCredits === null || isBlocked) return;
    
    // Verifica se acabou de cruzar o limite de 50 (de cima para baixo)
    const crossedThreshold50 = previousCredits > CREDIT_THRESHOLD_50 && credits <= CREDIT_THRESHOLD_50 && credits > CREDIT_THRESHOLD_30;
    
    // Verifica se acabou de cruzar o limite de 30 (de cima para baixo)
    const crossedThreshold30 = previousCredits > CREDIT_THRESHOLD_30 && credits <= CREDIT_THRESHOLD_30;
    
    // Sempre mostra abaixo de 30 (mas apenas uma vez por sessão)
    const belowAlwaysShow = credits < CREDIT_ALWAYS_SHOW && credits > 0;
    
    if (crossedThreshold50) {
      // Mostra popup quando chega a 50
      const shown50Key = 'creditWarning50Shown';
      if (!sessionStorage.getItem(shown50Key)) {
        sessionStorage.setItem(shown50Key, 'true');
        setTimeout(() => setShowLowCreditPopup(true), 500);
      }
    } else if (crossedThreshold30) {
      // Mostra popup quando chega a 30
      const shown30Key = 'creditWarning30Shown';
      if (!sessionStorage.getItem(shown30Key)) {
        sessionStorage.setItem(shown30Key, 'true');
        setTimeout(() => setShowLowCreditPopup(true), 500);
      }
    } else if (belowAlwaysShow) {
      // Abaixo de 30, mostra sempre que diminuir (uma vez por valor)
      const shownKey = `creditWarningBelow30_${Math.floor(credits / 5) * 5}`;
      if (!sessionStorage.getItem(shownKey)) {
        sessionStorage.setItem(shownKey, 'true');
        setTimeout(() => setShowLowCreditPopup(true), 500);
      }
    }
  }, [credits, previousCredits, isBlocked]);

  // Gerenciar aviso visual de créditos baixos (banner/badge)
  useEffect(() => {
    if (shouldShowWarning && !showCreditWarning) {
      setShowCreditWarning(true);
    } else if (!shouldShowWarning) {
      setShowCreditWarning(false);
    }
  }, [shouldShowWarning, showCreditWarning]);

  // Carregar planos de assinatura
  useEffect(() => {
    const loadSubscriptionPlans = async () => {
      try {
        const response = await fetch('/api/v1/subscriptions/plans');

        if (response.ok) {
          const plans = await response.json();
          setSubscriptionPlans(plans);
        } else {
          console.error('Erro ao carregar planos - response não ok:', response.status);
        }
      } catch (error) {
        console.error('Erro ao carregar planos:', error);
      }
    };

    loadSubscriptionPlans();
  }, []);

  // Carregar assinatura atual
  useEffect(() => {
    const loadCurrentSubscription = async () => {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch('/api/v1/subscriptions/my-subscription', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const subscription = await response.json();
          setCurrentSubscription(subscription);
        } else {
          setCurrentSubscription(null);
        }
      } catch (error) {
        console.error('Erro ao carregar assinatura:', error);
        setCurrentSubscription(null);
      }
    };

    if (user) {
      loadCurrentSubscription();
    }
  }, [user]);

  const value = {
    credits,
    setCredits,
    showLowCreditPopup,
    setShowLowCreditPopup,
    showCreditWarning,
    setShowCreditWarning,
    isBlocked,
    shouldShowWarning,
    subscriptionPlans,
    setSubscriptionPlans,
    currentSubscription,
    setCurrentSubscription,
    overdraftUsed: user?.overdraft_used || false
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};

export const useCredit = () => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredit must be used within a CreditProvider');
  }
  return context;
}; 